import { InfoSet } from "../InfoSets/InfoSet";
import { Dictionary } from "../Utils/Dictionary";
import { HashSet } from "../Utils/HashSet";
import { IpAddress, IpVersion } from "../Core/IpAddress";
import { MacAddress } from "../Core/MacAddress";
import { Device } from "../Core/Device";
import { List } from "../Utils/List";
import { BaseBridgeAddressTable } from "../InfoSets/BaseBridgeAddressTable";
import { IfEntry, IfTable } from "../InfoSets/IfTable";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { ValueEncoder } from "../Utils/ValueEncoder";

export class StpPortEntry implements ObjectSerializer {

	private static dot1dStpPortState_unknown = 0;
	private static dot1dStpPortState_disabled = 1;
	private static dot1dStpPortState_blocking = 2;
	private static dot1dStpPortState_listening = 3;
	private static dot1dStpPortState_learning = 4;
	private static dot1dStpPortState_forwarding = 5;
	private static dot1dStpPortState_broken = 6;

	public Index: number;
	public State: number;
	public StateAsString(): string {
		var stateStr = '';

		switch (this.State) {
			case StpPortEntry.dot1dStpPortState_unknown: stateStr = "Unknown"; break;
			case StpPortEntry.dot1dStpPortState_disabled: stateStr = "Disabled"; break;
			case StpPortEntry.dot1dStpPortState_blocking: stateStr = "Blocking"; break;
			case StpPortEntry.dot1dStpPortState_listening: stateStr = "Listening"; break;
			case StpPortEntry.dot1dStpPortState_learning: stateStr = "Learning"; break;
			case StpPortEntry.dot1dStpPortState_forwarding: stateStr = "Forwarding"; break;
			case StpPortEntry.dot1dStpPortState_broken: stateStr = "Broken"; break;
			default: stateStr = "Unknown"; break;
		}

		return stateStr;
	}

	public DesignatedRoot: MacAddress;
	public get HasDesignatedRoot(): boolean {
		return this.DesignatedRoot && !this.DesignatedRoot.IsNull;
	}

	public DesignatedBridge: MacAddress;
	public get HasDesignatedBridge(): boolean {
		return this.DesignatedBridge && !this.DesignatedBridge.IsNull;
	}

	// DesignatedPort (determined by inspecting LB/HB of SNMP response)
	public DesignatedPort: number;
	public get HasDesignatedPort(): boolean {
		return this.DesignatedPort >= 0;
	}

	// DesignatedPort storage/options
	public DesignatedPort_HB: number;
	public DesignatedPort_LB: number;

	public DesignatedPort_HBF: number;
	public DesignatedPort_LBF: number;
	// CACHE
	public DesignatedRootDevice: Device;
	public DesignatedBridgeDevice: Device;

	constructor(bpIndex?: number) {
		this.Index = bpIndex;
		this.State = StpPortEntry.dot1dStpPortState_unknown;

		this.DesignatedRoot = null;
		this.DesignatedBridge = null;

		this.DesignatedPort = -1;
		this.DesignatedPort_HB = 0;
		this.DesignatedPort_LB = 0;

		this.DesignatedPort_HBF = -1;
		this.DesignatedPort_LBF = -1;

		this.DesignatedRootDevice = null;
		this.DesignatedBridgeDevice = null;
	}

	public ClearRemoteCache(): void {
		this.DesignatedRootDevice = null;
		this.DesignatedBridgeDevice = null;
	}

	public SetDesignatedPort(bpIndex: number, highByte: number, lowByte: number): void {
		this.DesignatedPort = bpIndex;

		this.DesignatedPort_HB = highByte;
		this.DesignatedPort_LB = lowByte;

		// RECALC
		this.DesignatedPort_HBF = this.ConvertDesignatedPort_HighByteFirst();
		this.DesignatedPort_LBF = this.ConvertDesignatedPort_LowByteFirst();

		// NOTE: Don't SET the DesignatedPort (it is passed in by bpIndex)
	}

	public SetDesignatedPort_HBF(): void {
		if (this.DesignatedPort_HBF >= 0)
			this.DesignatedPort = this.DesignatedPort_HBF;
	}

	public SetDesignatedPort_LBF(): void {
		if (this.DesignatedPort_LBF >= 0)
			this.DesignatedPort = this.DesignatedPort_LBF;
	}

	private ConvertDesignatedPort_HighByteFirst(): number {
		var bpIndex = 0;

		// STANDARD ORDER: HIGH BYTE (first) LOW BYTE (second)
		var highByte = this.DesignatedPort_HB & 0x0F;
		var lowByte = this.DesignatedPort_LB;

		bpIndex = ((highByte << 8) | lowByte);
		if (bpIndex < 0)
			bpIndex = this.DesignatedPort_HB;

		return (bpIndex);
	}

	private ConvertDesignatedPort_LowByteFirst(): number {
		var bpIndex = 0;

		// STANDARD ORDER: HIGH BYTE (first) LOW BYTE (second)
		// REVERSE ORDER:  LOW BYTE (first) HIGH BYTE (second)
		var lowByte = this.DesignatedPort_HB;
		var highByte = this.DesignatedPort_LB & 0x0F;

		bpIndex = ((highByte << 8) | lowByte);
		if (bpIndex < 0)
			bpIndex = this.DesignatedPort_HB;

		return (bpIndex);
	}

	public Write(obj: any, context: any): void {
		obj.stp_port_entry = {};
		obj = obj.stp_port_entry;

		obj.index = ValueEncoder.EncodeInteger(this.Index);
		obj.state = ValueEncoder.EncodeInteger(this.State);

		if (this.HasDesignatedRoot)
			obj.designated_root = ValueEncoder.EncodeMacAddress(this.DesignatedRoot);

		if (this.HasDesignatedBridge)
			obj.designated_bridge = ValueEncoder.EncodeMacAddress(this.DesignatedBridge);

		if (this.HasDesignatedPort) {
			obj.designated_port = ValueEncoder.EncodeInteger(this.DesignatedPort);
			obj.designated_port_hb = ValueEncoder.EncodeInteger(this.DesignatedPort_HB);
			obj.designated_port_lb = ValueEncoder.EncodeInteger(this.DesignatedPort_LB);
		}
	}

	public Read(obj: any, context: any): void {
		if (!obj.stp_port_entry)
			throw new Error();
		obj = obj.stp_port_entry;

		let designatedPort = -1;
		let designatedPort_HB = 0;
		let designatedPort_LB = 0;

		if (obj.index !== undefined)
			this.Index = ValueEncoder.DecodeInteger(obj.index);

		if (obj.state !== undefined)
			this.State = ValueEncoder.DecodeInteger(obj.state);

		if (obj.designated_root !== undefined)
			this.DesignatedRoot = ValueEncoder.DecodeMacAddress(obj.designated_root);

		if (obj.designated_bridge !== undefined)
			this.DesignatedBridge = ValueEncoder.DecodeMacAddress(obj.designated_bridge);

		if (obj.designated_port !== undefined)
			this.DesignatedPort = ValueEncoder.DecodeInteger(obj.designated_port);

		if (obj.designated_port_hb !== undefined)
			this.DesignatedPort_HB = ValueEncoder.DecodeInteger(obj.designated_port_hb);

		if (obj.designated_port_lb !== undefined)
			this.DesignatedPort_LB = ValueEncoder.DecodeInteger(obj.designated_port_lb);

		// ONE SET
		this.SetDesignatedPort(designatedPort, designatedPort_HB, designatedPort_LB);
	}
}

export class StpPortTable extends InfoSet {
	private _stpPortTable: Dictionary<number, StpPortEntry>;

	public BaseBridgeAddresses: HashSet<MacAddress>;

	constructor(device: Device) {
		super(device);
		this._stpPortTable = new Dictionary<number, StpPortEntry>();
		this.BaseBridgeAddresses = new HashSet<MacAddress>();
		return;
	}

	public Add(bpIndex:number): StpPortEntry {
		var stpEntry: StpPortEntry = this._stpPortTable.Item(bpIndex);

		if(!stpEntry) {
			stpEntry = new StpPortEntry(bpIndex);
			this._stpPortTable.Add(bpIndex, stpEntry);
		}

		return stpEntry;
	}

	private AddEntry(stpEntry: StpPortEntry): void {
		this._stpPortTable.Add(stpEntry.Index, stpEntry);
	}

	public Clear():void {
		this._stpPortTable.Clear();
	}

	public get Count(): number {
		return this._stpPortTable.Count
	}

	public Find(bpIndex: number): StpPortEntry {
		return this._stpPortTable.Item(bpIndex);
	}

	public Remove(bpIndex:number):void {
		this._stpPortTable.Remove(bpIndex);
	}

	public RemoveEntry(bpEntry: StpPortEntry):void {
		if (bpEntry)
			this._stpPortTable.Remove(bpEntry.Index);
	}

	private IsLocalDevice(designatedBridge: MacAddress):boolean {
		return this.BaseBridgeAddresses.Contains(designatedBridge);
	}

	private UseHighByteFirst(): boolean {
		var useHighByteFirst = true;

		var highByteCount = 0;
		var lowByteCount = 0;

		this._stpPortTable.ForEach((pair) => {
			if (!this.IsLocalDevice(pair.Value.DesignatedBridge))
				return;

			if (pair.Value.DesignatedPort_HBF == pair.Value.Index)
				highByteCount++;
			else if (pair.Value.DesignatedPort_LBF == pair.Value.Index)
				lowByteCount++;
			else {
				// TODO: Test to see if the HBF/LBF is recognized as a VALID port number on the LOCAL device
			}
		});

		return (useHighByteFirst);
	}

	public ClearRemoteCache(): void {
		this._stpPortTable.ForEach((pair) => {
			pair.Value.ClearRemoteCache();
		});
	}

	public ValidateEntries():void {
		var useHighByteFirst:boolean = this.UseHighByteFirst();

		// ENSURE that ALL of the potential BASE BRIDGE ADDRESSES are registered with the DEVICE
		this.VerifyBaseBridgeAddresses();

		// SEARCH for INVALID/REDUNDANT entries that don't provide any USEFUL spanning-tree sighting information
		var invalidEntries = new List<StpPortEntry>();

		this._stpPortTable.ForEach((pair) => {
			if (!this.IsLocalDevice(pair.Value.DesignatedBridge))
				return;

			if (pair.Value.Index == pair.Value.DesignatedPort)
				invalidEntries.Add(pair.Value);
		});

		invalidEntries.ForEach((stpEntry) => {
			this.RemoveEntry(stpEntry);
		});
	}

	public VerifyBaseBridgeAddresses(): void {
		var deviceMacAddresses: HashSet<MacAddress> = null;
		var bbaTable: BaseBridgeAddressTable = null;
		var baseMacAddress: MacAddress = null;

		if (this.Device.HasBaseBridgeAddressTable) {
			bbaTable = this.Device.BaseBridgeAddressTable;
			deviceMacAddresses = bbaTable.MacAddresses;
		}

		if ((deviceMacAddresses == null) || (deviceMacAddresses.Count <= 0)) {
			if (this.Device.HasIfTable) {
				var ifTable: IfTable = this.Device.IfTable;
				deviceMacAddresses = ifTable.MacAddresses;
			}
		}

		if (this.Device.HasMacAddress)
			baseMacAddress = this.Device.MacAddress;

		var bridgeMacAddress_PlusOne: MacAddress = null;
		var bridgeMacAddress_MinusOne: MacAddress = null;
		var addBridgeAddress = false;

		this._stpPortTable.ForEach((pair) => {
			var stpEntry: StpPortEntry = pair.Value;

			addBridgeAddress = false;

			// CHECK if we ALREADY know about this BRIDGE ADDDRESS
			if (this.IsLocalDevice(stpEntry.DesignatedBridge))
				return;

			// IF the PORTS are pointing at different ports; not going to help us
			if (stpEntry.Index != stpEntry.DesignatedPort)
				return;

			// BUILD a MAC ADDRESS that is PLUS ONE && MINUS ONE (to see if we can find a MATCH)
			bridgeMacAddress_PlusOne = stpEntry.DesignatedBridge.Clone();
			bridgeMacAddress_PlusOne.Increment();

			bridgeMacAddress_MinusOne = stpEntry.DesignatedBridge.Clone();
			bridgeMacAddress_MinusOne.Decrement();

			if (deviceMacAddresses != null) {
				if (deviceMacAddresses.Contains(bridgeMacAddress_PlusOne))
					addBridgeAddress = true;
				else if (deviceMacAddresses.Contains(bridgeMacAddress_MinusOne))
					addBridgeAddress = true;
			}

			if (baseMacAddress != null) {
				if (bridgeMacAddress_PlusOne.Equals(baseMacAddress))
					addBridgeAddress = true;
				else if (bridgeMacAddress_MinusOne.Equals(baseMacAddress))
					addBridgeAddress = true;
			}

			if (addBridgeAddress) {
				if (!this.BaseBridgeAddresses.Contains(stpEntry.DesignatedBridge))
					this.BaseBridgeAddresses.Add(stpEntry.DesignatedBridge);

				if (bbaTable == null)
					bbaTable = this.Device.AddBaseBridgeAddressTable();

				if (!bbaTable.Contains(stpEntry.DesignatedBridge))
					bbaTable.Add(stpEntry.DesignatedBridge);
			}
		});
	}

	public VerifyDesignatedPorts():void {
		var useHighByteFirst:boolean = this.UseHighByteFirst();

		this._stpPortTable.ForEach((pair) => {
			if (useHighByteFirst)
				pair.Value.SetDesignatedPort_HBF();
			else
				pair.Value.SetDesignatedPort_LBF();
		});
	}

	public get HasMacSightings():boolean {
		return true;
	}

	public get MacSightings(): HashSet<MacAddress> {
		var macAddresses = new HashSet<MacAddress>();

		this._stpPortTable.ForEach((pair) => {
			if (pair.Value.HasDesignatedRoot) {
				macAddresses.Add(pair.Value.DesignatedRoot);
			}

			if (pair.Value.HasDesignatedBridge) {
				macAddresses.Add(pair.Value.DesignatedBridge);
			}
		});

		return (macAddresses);
	}

	public ForEach(callbackfn: (value: StpPortEntry, index: number, array: StpPortEntry[]) => void, thisArg?: any): void {
		this._stpPortTable.Values().ForEach(callbackfn, thisArg);
	}

	public Some(callbackfn: (value: StpPortEntry, index: number, array: StpPortEntry[]) => boolean, thisArg?: any): boolean {
		return this._stpPortTable.Values().Some(callbackfn, thisArg);
	}

	public Write(obj: any, context: any): void {
		obj.stp_port_info = {};
		obj = obj.stp_port_info;

		obj.entries = [];
		ObjSerUtils.WriteObjSerCollection(obj.entries, this._stpPortTable.Values(), context);
	}

	public Read(obj: any, context: any): void {
		if (!obj.stp_port_info)
			throw new Error();
		obj = obj.stp_port_info;

		this.Clear();

		if (obj.entries !== undefined) {
			obj.entries.forEach((value: any) => {
				let entry = new StpPortEntry();
				entry.Read(value, context);
				this.AddEntry(entry);
			}, this);
		}
	}
}
